/** 設定(suite:admin)。秘密は伏字で返るので、伏字のまま保存しても壊れない。 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Space,
  Table,
  Typography,
  message,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import { HelpSettingsCard } from "../shell/help/HelpSettingsCard";
import { useApi } from "../lib/useApi";
import type { SettingRow } from "../lib/api";
import { jst } from "../lib/format";

const { Paragraph, Text } = Typography;

/** 個人情報の保有期間。server/app/core/settings_service.py と同じキー・同じ上限。 */
const RETENTION_KEY = "retention";
const RETENTION_FIELD = "days_after_offboard";
const RETENTION_MAX = 3650;

export function SettingsPage() {
  const api = useApi();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<{ key: string; json: string } | null>(null);
  const [jsonError, setJsonError] = useState("");
  const [days, setDays] = useState<number | null>(null);

  const query = useQuery({ queryKey: ["settings"], queryFn: api.settings });

  const save = useMutation({
    mutationFn: ({ key, data }: { key: string; data: Record<string, unknown> }) =>
      api.saveSetting(key, data),
    onSuccess: () => {
      void message.success("保存した(監査ログに記録した)");
      setEditing(null);
      void qc.invalidateQueries({ queryKey: ["settings"] });
      void qc.invalidateQueries({ queryKey: ["audit"] });
    },
    onError: (e: unknown) => {
      void message.error(e instanceof Error ? e.message : String(e));
    },
  });

  // 保有期間だけは JSON を触らせない(消えたら戻せない設定なので、型と範囲を画面で縛る)
  const retention = (query.data?.items ?? []).find((r) => r.key === RETENTION_KEY);
  const savedDays = Number(retention?.value?.[RETENTION_FIELD] ?? 0) || 0;
  useEffect(() => {
    setDays(savedDays);
  }, [savedDays]);

  const columns: ColumnsType<SettingRow> = [
    { title: "キー", dataIndex: "key", width: 240 },
    {
      title: "値",
      width: 360,
      ellipsis: true,
      onCell: (row) => ({ title: JSON.stringify(row.value) }),
      dataIndex: "value",
      render: (v: Record<string, unknown>) => (
        <Text code style={{ whiteSpace: "pre-wrap" }}>
          {JSON.stringify(v)}
        </Text>
      ),
    },
    {
      title: "更新日時 (JST)",
      dataIndex: "updated_at",
      width: 180,
      render: (v: string | null) => jst(v),
    },
    {
      title: "",
      width: 90,
      render: (_: unknown, row: SettingRow) => (
        <Button
          size="small"
          onClick={() => {
            setJsonError("");
            setEditing({ key: row.key, json: JSON.stringify(row.value, null, 2) });
          }}
        >
          編集
        </Button>
      ),
    },
  ];

  const submit = () => {
    if (!editing) return;
    let data: Record<string, unknown>;
    try {
      data = JSON.parse(editing.json || "{}") as Record<string, unknown>;
    } catch (e) {
      setJsonError(e instanceof Error ? e.message : "JSON として読めない");
      return;
    }
    save.mutate({ key: editing.key, data });
  };

  return (
    <div style={{ padding: 24 }}>
      <Typography.Title level={3}>設定</Typography.Title>
      <Paragraph type="secondary">
        値は JSON。秘密の項目(client_secret / password など)は「•••設定済み」と表示され、
        そのまま保存しても上書きされない。項目ごとのフォームは R2 で作る。
      </Paragraph>

      {query.isError && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message="設定を取得できなかった"
          description={query.error instanceof Error ? query.error.message : String(query.error)}
        />
      )}

      <HelpSettingsCard />

      <Card
        size="small"
        title="個人情報の保有期間(retention)"
        style={{ marginBottom: 16 }}
        extra={
          <Text type="secondary" style={{ fontSize: 12 }}>
            更新日時 (JST): {jst(retention?.updated_at) || "—"}
          </Text>
        }
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          退職手続き(退社申請のタスク)が全部終わってから、ここで決めた日数が過ぎた退職者を
          日次のジョブが自動で匿名化する。<Text strong>0 なら自動では匿名化しない。</Text>
          手続きを通っていない人と、タスクが残っている人は日数が過ぎても触らない。対象の一覧は{" "}
          <Link to="/hr/anonymize">人事 &gt; 匿名化</Link> で見える。
        </Paragraph>
        <Space align="center" wrap>
          <InputNumber
            min={0}
            max={RETENTION_MAX}
            step={30}
            value={days}
            addonAfter="日"
            style={{ width: 160 }}
            aria-label="退職からの保有日数"
            onChange={(v) => setDays(typeof v === "number" ? v : null)}
          />
          <Button
            type="primary"
            loading={save.isPending}
            disabled={days === null || days === savedDays}
            onClick={() =>
              save.mutate({ key: RETENTION_KEY, data: { [RETENTION_FIELD]: days ?? 0 } })
            }
          >
            保存
          </Button>
          <Text type="secondary">
            0〜{RETENTION_MAX} 日(10年)。現在の設定: {savedDays} 日
            {savedDays === 0 ? "(自動匿名化は停止中)" : ""}
          </Text>
        </Space>
      </Card>

      <Card
        size="small"
        title="登録済みの設定"
        extra={
          <Button
            type="primary"
            onClick={() => {
              setJsonError("");
              setEditing({ key: "", json: "{}" });
            }}
          >
            キーを追加
          </Button>
        }
      >
        <Table<SettingRow>
          scroll={{ x: "max-content" }}
          rowKey="key"
          size="small"
          columns={columns}
          dataSource={query.data?.items ?? []}
          loading={query.isFetching}
          pagination={false}
          locale={{ emptyText: "まだ設定が無い" }}
        />
      </Card>

      <Modal
        open={editing !== null}
        title={editing?.key ? `設定を編集: ${editing.key}` : "設定を追加"}
        onCancel={() => setEditing(null)}
        onOk={submit}
        confirmLoading={save.isPending}
        okText="保存"
        cancelText="やめる"
      >
        <Form layout="vertical">
          <Form.Item label="キー" required>
            <Input
              value={editing?.key ?? ""}
              placeholder="mail / notify / jobs など"
              onChange={(e) => setEditing((s) => (s ? { ...s, key: e.target.value } : s))}
            />
          </Form.Item>
          <Form.Item
            label="値 (JSON)"
            validateStatus={jsonError ? "error" : undefined}
            help={jsonError || undefined}
          >
            <Input.TextArea
              rows={8}
              value={editing?.json ?? ""}
              onChange={(e) => setEditing((s) => (s ? { ...s, json: e.target.value } : s))}
            />
          </Form.Item>
          <Space />
        </Form>
      </Modal>
    </div>
  );
}
